import { Container, Graphics } from 'pixi.js';
import { REVENGE_INTEL } from '@wooorms/shared';

/**
 * RevengeIntelArrow - Directional arrow pointing toward the player's killer
 *
 * After respawning, players see a compass-style arrow pointing toward
 * their killer for a limited time, giving them a chance for revenge.
 *
 * Features:
 * - Shows arrow at screen edge pointing toward killer
 * - Fades out after duration
 * - Updates direction in real-time as killer moves
 */
export class RevengeIntelArrow {
  /** Main container for the arrow UI */
  public container: Container;

  /** Arrow graphics */
  private arrow: Graphics;

  /** Whether the arrow is currently active */
  private active: boolean = false;

  /** Time remaining before arrow disappears (seconds) */
  private remainingTime: number = 0;

  /** Callback to get killer's current position */
  private getKillerPosition: (() => { x: number; y: number } | null) | null = null;

  /** Screen dimensions for positioning */
  private screenWidth: number = 1920;
  private screenHeight: number = 1080;

  /** Distance from screen edge for arrow placement */
  private edgePadding: number = 60;

  constructor() {
    this.container = new Container();
    this.container.label = 'revenge-intel-arrow';
    this.container.visible = false;

    // Create arrow graphics
    this.arrow = new Graphics();
    this.container.addChild(this.arrow);

    this.drawArrow();
  }

  /**
   * Draw the arrow shape
   */
  private drawArrow(): void {
    const size = REVENGE_INTEL.ARROW_SIZE;
    const color = REVENGE_INTEL.ARROW_COLOR;

    this.arrow.clear();

    // Draw arrow pointing right (will be rotated to point at target)
    // Arrow shape: >
    this.arrow.moveTo(size, 0); // Tip
    this.arrow.lineTo(-size / 2, -size / 2); // Top back
    this.arrow.lineTo(-size / 4, 0); // Inner notch
    this.arrow.lineTo(-size / 2, size / 2); // Bottom back
    this.arrow.closePath();
    this.arrow.fill({ color, alpha: 0.9 });
    this.arrow.stroke({ color: 0xffffff, width: 2, alpha: 0.6 });

    // Add glow effect
    this.arrow.circle(0, 0, size * 0.3);
    this.arrow.fill({ color, alpha: 0.4 });
  }

  /**
   * Set screen dimensions for positioning
   */
  setScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
  }

  /**
   * Start showing the revenge arrow
   * @param getKillerPosition Callback that returns the killer's current position
   */
  show(getKillerPosition: () => { x: number; y: number } | null): void {
    this.active = true;
    this.remainingTime = REVENGE_INTEL.ARROW_DURATION;
    this.getKillerPosition = getKillerPosition;
    this.container.visible = true;
    this.container.alpha = 1;
  }

  /**
   * Hide the arrow immediately
   */
  hide(): void {
    this.active = false;
    this.remainingTime = 0;
    this.getKillerPosition = null;
    this.container.visible = false;
  }

  /**
   * Check if arrow is currently active
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * Update the arrow position and fade
   * @param dt Delta time in seconds
   * @param playerX Player's current X position
   * @param playerY Player's current Y position
   */
  update(dt: number, playerX: number, playerY: number): void {
    if (!this.active) return;

    // Update remaining time
    this.remainingTime -= dt;

    if (this.remainingTime <= 0) {
      this.hide();
      return;
    }

    // Get killer position
    const killerPos = this.getKillerPosition?.();
    if (!killerPos) {
      // Killer no longer exists, hide arrow
      this.hide();
      return;
    }

    // Calculate direction to killer
    const dx = killerPos.x - playerX;
    const dy = killerPos.y - playerY;
    const angle = Math.atan2(dy, dx);

    // Position arrow at screen edge in direction of killer
    // Calculate where the line from center to killer intersects screen edge
    const centerX = this.screenWidth / 2;
    const centerY = this.screenHeight / 2;

    // Use smaller dimension to ensure arrow stays on screen
    const maxDist = Math.min(centerX, centerY) - this.edgePadding;

    const arrowX = centerX + Math.cos(angle) * maxDist;
    const arrowY = centerY + Math.sin(angle) * maxDist;

    this.container.position.set(arrowX, arrowY);
    this.arrow.rotation = angle;

    // Fade out during the last ARROW_FADE_TIME seconds
    if (this.remainingTime < REVENGE_INTEL.ARROW_FADE_TIME) {
      this.container.alpha = this.remainingTime / REVENGE_INTEL.ARROW_FADE_TIME;
    } else {
      this.container.alpha = 1;
    }

    // Pulse effect for urgency
    const pulse = Math.sin(Date.now() * 0.01) * 0.1 + 0.9;
    this.arrow.scale.set(pulse);
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.arrow.destroy();
    this.container.destroy({ children: true });
  }
}
